import type { EvidenceItem } from "../types";

export type CiteFormat = "mla" | "apa" | "bibtex";

export const CITE_LABELS: Record<CiteFormat, string> = {
  mla: "MLA",
  apa: "APA",
  bibtex: "BibTeX",
};

interface Name {
  last: string;
  first: string;
}

const PREPRINTS: Record<string, string> = { arxiv: "arXiv", biorxiv: "bioRxiv", medrxiv: "medRxiv" };

/** "Smith, John" · "Smith JA" · "John A. Smith" → surname + given names. */
function parseName(raw: string): Name {
  const s = raw.trim();
  const comma = s.indexOf(",");
  if (comma > 0) return { last: s.slice(0, comma).trim(), first: s.slice(comma + 1).trim() };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { last: s, first: "" };
  const tail = parts[parts.length - 1];
  // PubMed style: initials after the surname ("Smith JA").
  if (/^[A-Z]{1,3}$/.test(tail)) return { last: parts.slice(0, -1).join(" "), first: tail };
  return { last: tail, first: parts.slice(0, -1).join(" ") };
}

/** The service sends up to two names joined by "; ", or "First Author et al." for three or more. */
function parseAuthors(authors: string | null | undefined): { names: Name[]; etAl: boolean } {
  if (!authors) return { names: [], etAl: false };
  const etAl = /\s+et al\.?$/.test(authors);
  const names = authors
    .replace(/\s+et al\.?$/, "")
    .split(";")
    .map((a) => a.trim())
    .filter(Boolean)
    .map(parseName);
  return { names, etAl };
}

const initials = (first: string): string =>
  first
    .split(/[\s.-]+/)
    .filter(Boolean)
    .map((p) => (/^[A-Z]{2,3}$/.test(p) ? p.split("").map((c) => `${c}.`).join(" ") : `${p[0]}.`))
    .join(" ");

const year = (m: EvidenceItem): number | null =>
  m.pub_year ?? (m.pub_date ? Number(m.pub_date.slice(0, 4)) || null : null);

const venue = (m: EvidenceItem): string | null => {
  const j = m.journal?.trim();
  if (j) return PREPRINTS[j.toLowerCase()] ?? j;
  return PREPRINTS[m.source ?? ""] ?? null;
};

const link = (m: EvidenceItem): string | null => {
  if (m.doi) return `https://doi.org/${m.doi}`;
  const id = m.doc_id ?? "";
  if (/^PMC\d+$/.test(id)) return `https://www.ncbi.nlm.nih.gov/pmc/articles/${id}/`;
  if (m.source === "arxiv") return `https://arxiv.org/abs/${id}`;
  if (m.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${m.pmid}/`;
  return null;
};

/** Ends a sentence unless the text already ends in punctuation. */
const stop = (s: string): string => (/[.?!]$/.test(s) ? s : `${s}.`);

function mla(m: EvidenceItem): string {
  const { names, etAl } = parseAuthors(m.authors);
  const given = (n: Name) => (/^[A-Z]{1,3}$/.test(n.first) ? initials(n.first) : n.first);
  const [a, b] = names;
  const first = a ? (a.first ? `${a.last}, ${given(a)}` : a.last) : "";
  let who = "";
  if (a && (etAl || names.length > 2)) who = `${first}, et al.`;
  else if (a && b) who = `${first}, and ${b.first ? `${given(b)} ${b.last}` : b.last}.`;
  else if (a) who = stop(first);
  const parts = [who, m.title ? `“${stop(m.title.trim())}”` : ""];
  const tail = [venue(m), year(m)?.toString(), link(m)].filter(Boolean).join(", ");
  return [...parts, tail ? `${tail}.` : ""].filter(Boolean).join(" ");
}

function apa(m: EvidenceItem): string {
  const { names, etAl } = parseAuthors(m.authors);
  const fmt = (n: Name) => (n.first ? `${n.last}, ${initials(n.first)}` : n.last);
  let who = names.map(fmt).join(names.length === 2 && !etAl ? ", & " : ", ");
  if (etAl) who += ", et al.";
  const y = year(m);
  const parts = [
    who ? stop(who) : "",
    `(${y ?? "n.d."}).`,
    m.title ? stop(m.title.trim()) : "",
    venue(m) ? `${venue(m)}.` : "",
    link(m) ?? "",
  ];
  return parts.filter(Boolean).join(" ");
}

const bibEscape = (s: string): string => s.replace(/([&%$#_])/g, "\\$1");

function bibtex(m: EvidenceItem): string {
  const { names, etAl } = parseAuthors(m.authors);
  const y = year(m);
  const word = (m.title ?? "").toLowerCase().match(/[a-z]{4,}/)?.[0] ?? "paper";
  const lastKey = (names[0]?.last ?? "anon").toLowerCase().replace(/[^a-z]/g, "") || "anon";
  const authors = names.map((n) => (n.first ? `${n.last}, ${n.first}` : n.last));
  if (etAl) authors.push("others");
  const preprint = m.source === "arxiv" || PREPRINTS[(m.journal ?? "").toLowerCase()];
  const fields: [string, string | null][] = [
    ["title", m.title ? `{${bibEscape(m.title.trim())}}` : null],
    ["author", authors.length ? bibEscape(authors.join(" and ")) : null],
    [preprint ? "howpublished" : "journal", venue(m) ? bibEscape(venue(m) as string) : null],
    ["year", y?.toString() ?? null],
    ["doi", m.doi ?? null],
    ["eprint", m.source === "arxiv" ? m.doc_id : null],
    ["archivePrefix", m.source === "arxiv" ? "arXiv" : null],
    ["pmid", m.pmid ?? null],
    ["url", link(m)],
  ];
  const body = fields
    .filter(([, v]) => v)
    .map(([k, v]) => `  ${k} = {${v}}`)
    .join(",\n");
  return `@${preprint ? "misc" : "article"}{${lastKey}${y ?? ""}${word},\n${body}\n}`;
}

export function formatCitation(m: EvidenceItem, format: CiteFormat): string {
  switch (format) {
    case "mla":
      return mla(m);
    case "apa":
      return apa(m);
    case "bibtex":
      return bibtex(m);
  }
}
